import { ref, computed } from 'vue';
import type { OTPFormFieldExecOptions } from '../types/formTypeStructure';
import { useFormStore } from '../store/useFormStore';

/**
 * Token d'authentification flexible - peut contenir n'importe quelle structure
 * On cherche dynamiquement les clés contenant "token"
 */
export type OTPAuthToken = Record<string, any> & {
  token_type?: string; // "bearer" par défaut
  expires_in?: number; // en secondes
  expires_at?: number; // timestamp Unix (calculé depuis expires_in)
};

export interface OTPServerResponse {
  success: boolean;
  call_back_url?: string;
  message?: string;
  // Token d'authentification extrait dynamiquement
  authToken?: OTPAuthToken;
}

const isMap = (
  payload?: Map<string, unknown> | Record<string, unknown>
): payload is Map<string, unknown> => payload instanceof Map;

const normalizePayload = (
  payload?: Map<string, unknown> | Record<string, unknown>
): Record<string, unknown> => {
  if (!payload) {
    return {};
  }
  if (isMap(payload)) {
    return Object.fromEntries(payload.entries());
  }
  return { ...payload };
};

const buildAbsoluteUrl = (serverDns: string, endpoint: string): string => {
  const base = serverDns.replace(/\/+$/, "");
  if (/^https?:\/\//i.test(endpoint)) {
    return endpoint;
  }
  const path = endpoint.replace(/^\/+/, "");
  return `${base}/${path}`;
};

const withTimeout = <T,>(
  promise: Promise<T>,
  timeoutMs?: number,
  controller?: AbortController
): Promise<T> => {
  if (!timeoutMs || timeoutMs <= 0 || !controller) {
    return promise;
  }

  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  return promise.finally(() => {
    clearTimeout(timeout);
  });
};

/**
 * Récupère une valeur imbriquée depuis un objet en utilisant une clé avec points
 */
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

/**
 * Trouve toutes les clés contenant "token" (insensible à la casse)
 * et extrait les données du token depuis la réponse API
 */
function extractAuthToken(responseData: any): OTPAuthToken | null {
  if (!responseData || typeof responseData !== 'object') {
    return null;
  }

  // Chercher toutes les clés contenant "token"
  const tokenKeys: string[] = [];
  const tokenData: Record<string, any> = {};

  function findTokenKeys(obj: any, prefix = ''): void {
    if (obj === null || obj === undefined) return;
    
    if (typeof obj !== 'object') return;

    for (const [key, value] of Object.entries(obj)) {
      const fullKey = prefix ? `${prefix}.${key}` : key;
      const lowerKey = key.toLowerCase();

      // Si la clé contient "token", on la garde
      if (lowerKey.includes('token')) {
        tokenKeys.push(fullKey);
        tokenData[key] = value;
      }

      // Si la valeur est un objet, chercher récursivement
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        findTokenKeys(value, fullKey);
      }
    }
  }

  findTokenKeys(responseData);

  // Si aucune clé contenant "token" n'est trouvée
  if (tokenKeys.length === 0) {
    return null;
  }

  // Normaliser le token extrait
  const normalizedToken: OTPAuthToken = {
    ...tokenData,
  };

  // Normaliser token_type (chercher différentes variantes)
  if (!normalizedToken.token_type) {
    normalizedToken.token_type = 
      normalizedToken.token_type ||
      normalizedToken.tokenType ||
      normalizedToken.token_type ||
      'bearer';
  }

  // Normaliser expires_in
  if (!normalizedToken.expires_in) {
    normalizedToken.expires_in = 
      normalizedToken.expires_in ||
      normalizedToken.expiresIn ||
      normalizedToken.expires_in;
  }

  // Calculer expires_at si expires_in est disponible
  if (normalizedToken.expires_in && !normalizedToken.expires_at) {
    normalizedToken.expires_at = Date.now() + (normalizedToken.expires_in * 1000);
  }

  console.log('🔑 [OTP] Token extrait:', {
    keysFound: tokenKeys,
    normalizedToken: Object.keys(normalizedToken),
  });

  return normalizedToken;
}

/**
 * Normalise un token d'authentification
 */
function normalizeAuthToken(token: any): OTPAuthToken {
  const normalized: OTPAuthToken = {
    ...token,
    token_type: token.token_type || token.tokenType || token.token_type || 'bearer',
    expires_in: token.expires_in || token.expiresIn || token.expires_in,
  };
  
  // Calculer expires_at si expires_in est fourni
  if (normalized.expires_in && !normalized.expires_at) {
    normalized.expires_at = Date.now() + (normalized.expires_in * 1000);
  } else if (token.expires_at) {
    normalized.expires_at = token.expires_at;
  }
  
  return normalized;
}

/**
 * Récupère la valeur du token depuis l'objet OTPAuthToken
 * Cherche les clés communes : access_token, token, authToken, etc.
 */
function getTokenValue(token: OTPAuthToken): string | null {
  // Ordre de priorité pour trouver la valeur du token
  const possibleKeys = [
    'access_token',
    'accessToken',
    'token',
    'authToken',
    'auth_token',
    'bearer_token',
    'bearerToken',
  ];

  for (const key of possibleKeys) {
    if (token[key] && typeof token[key] === 'string') {
      return token[key];
    }
  }

  // Si aucune clé standard n'est trouvée, chercher la première clé contenant "token"
  for (const [key, value] of Object.entries(token)) {
    if (key.toLowerCase().includes('token') && typeof value === 'string' && value.length > 10) {
      return value;
    }
  }

  return null;
}

/**
 * Vérifie si le token est encore valide (non expiré)
 */
function isTokenValid(token: OTPAuthToken | null): boolean {
  if (!token) return false;
  
  // Si expires_at est défini, vérifier l'expiration
  if (token.expires_at) {
    return token.expires_at > Date.now();
  }
  
  // Si expires_in est défini mais pas expires_at, considérer comme valide
  // (on ne peut pas vérifier sans connaître la date de création)
  if (token.expires_in) {
    return true;
  }
  
  // Pas d'expiration définie = valide
  return true;
}

/**
 * Génère une clé unique pour stocker le token dans le store
 */
function getTokenStorageKey(options?: OTPFormFieldExecOptions, fieldName?: string): string {
  const serverDns = options?.serverDns || 'default';
  const field = fieldName || 'otp';
  return `${serverDns}_${field}`;
}

export const useOTPHandler = (options?: OTPFormFieldExecOptions, fieldName?: string) => {
  const formStore = useFormStore();
  const isSending = ref(false);
  const otpSent = ref(false);
  const otpValidated = ref(false);
  const serverResponse = ref<OTPServerResponse | null>(null);
  const error = ref<string | null>(null);
  const resendCooldown = ref<number>(0);
  const isResending = ref(false);

  let cooldownInterval: ReturnType<typeof setInterval> | null = null;

  // Clé unique pour stocker le token dans le store
  const tokenStorageKey = computed(() => getTokenStorageKey(options, fieldName));

  // Récupérer le token depuis le store
  const authToken = computed(() => formStore.getOTPToken(tokenStorageKey.value));

  // Fonctions pour gérer le token via le store
  const storeAuthToken = (token: OTPAuthToken): void => {
    console.log('💾 [OTP] Stockage du token avec la clé:', tokenStorageKey.value, 'Token:', token);
    formStore.setOTPToken(tokenStorageKey.value, token);
    // Vérifier que le token a bien été stocké
    const stored = formStore.getOTPToken(tokenStorageKey.value);
    console.log('✅ [OTP] Token stocké vérifié:', stored ? 'Oui' : 'Non', stored);
  };

  const getStoredAuthToken = (): OTPAuthToken | null => {
    return formStore.getOTPToken(tokenStorageKey.value);
  };

  const clearAuthToken = (): void => {
    formStore.clearOTPToken(tokenStorageKey.value);
  };

  const sendOTP = async (formData: Record<string, any>): Promise<OTPServerResponse> => {
    if (!options) {
      throw new Error("OTP options are not configured for this field");
    }

    isSending.value = true;
    error.value = null;

    try {
      const controller = new AbortController();
      
      // Utiliser sendOTPApiConfig si disponible, sinon utiliser la config par défaut
      const sendConfig = options.sendOTPApiConfig ?? {
        serverDns: options.serverDns,
        postApiEndPoint: options.postApiEndPoint,
        payload: options.payload,
        extraHeaders: options.extraHeaders,
        bearer: options.bearer,
      };
      
      const url = buildAbsoluteUrl(sendConfig.serverDns, sendConfig.postApiEndPoint);
      
      // Normaliser et fusionner le payload
      const basePayload = normalizePayload(sendConfig.payload);
      const mergedPayload = {
        ...basePayload,
        ...formData, // Fusionner avec les données du formulaire
      };

      // Préparer les headers
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(sendConfig.extraHeaders ?? {}),
      };

      if (sendConfig.bearer) {
        headers.Authorization = `Bearer ${sendConfig.bearer}`;
      }

      // Envoyer la requête POST
      const fetchPromise = fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(mergedPayload),
        signal: controller.signal,
      });

      const response = await withTimeout(
        fetchPromise,
        options.timeoutMs,
        controller
      );

      let payload: any = null;
      try {
        payload = await response.json();
      } catch {
        // Si la réponse n'est pas du JSON, essayer de lire le texte
        const text = await response.text();
        throw new Error(`Invalid JSON response: ${text}`);
      }

      if (!response.ok) {
        const message =
          payload?.message ??
          `OTP request failed with status ${response.status} ${response.statusText}`;
        throw new Error(message);
      }

      // Gestion spéciale pour httpbin.org (mock endpoint)
      let responseData = payload;
      if (payload?.json) {
        // Format httpbin.org
        responseData = payload.json;
      }

      // Simulation de réponse OTP pour les tests
      const isHttpbin = url.includes("httpbin.org");
      if (isHttpbin) {
        const otpResponse: OTPServerResponse = {
          success: true,
          message: "OTP vérifié avec succès (simulation httpbin)",
        };
        
        otpSent.value = true;
        serverResponse.value = otpResponse;
        return otpResponse;
      }

      // Construire la réponse standardisée pour les vrais endpoints
      const otpResponse: OTPServerResponse = {
        success: responseData?.success ?? true,
        call_back_url: responseData?.call_back_url ?? responseData?.callback_url ?? responseData?.callBackUrl,
        message: responseData?.message,
      };

      // Extraire et stocker le token si présent
      const extractedToken = extractAuthToken(responseData);
      if (extractedToken) {
        otpResponse.authToken = extractedToken;
        storeAuthToken(extractedToken);
        console.log('✅ [OTP] Token d\'authentification stocké en mémoire:', {
          key: tokenStorageKey.value,
          tokenKeys: Object.keys(extractedToken),
        });
      }

      otpSent.value = true;
      serverResponse.value = otpResponse;

      return otpResponse;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Unexpected OTP error";
      error.value = message;
      throw err;
    } finally {
      isSending.value = false;
    }
  };

  const handleCallbackRedirect = (url: string, clearForm: () => void) => {
    // Vider le formulaire
    clearForm();
    
    // Rediriger vers l'URL
    window.location.href = url;
  };

  const validateOTP = async (otpCode: string, formData: Record<string, any>): Promise<OTPServerResponse> => {
    if (!options) {
      throw new Error("OTP options are not configured for this field");
    }

    isSending.value = true;
    error.value = null;

    try {
      const controller = new AbortController();
      const url = buildAbsoluteUrl(options.serverDns, options.postApiEndPoint);
      
      // Normaliser et fusionner le payload
      const basePayload = normalizePayload(options.payload);
      
      // Pour les endpoints OSC, utiliser "code" au lieu de "otpCode"
      const isOSCEndpoint = url.includes('/osc/auth/verify-otp');
      const codeFieldName = isOSCEndpoint ? 'code' : 'otpCode';
      
      const mergedPayload = {
        ...basePayload,
        ...formData,
        [codeFieldName]: otpCode, // Ajouter le code OTP au payload avec le bon nom de champ
      };

      // Préparer les headers
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options.extraHeaders ?? {}),
      };

      if (options.bearer) {
        headers.Authorization = `Bearer ${options.bearer}`;
      }

      // Envoyer la requête POST
      const fetchPromise = fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(mergedPayload),
        signal: controller.signal,
      });

      const httpResponse = await withTimeout(
        fetchPromise,
        options.timeoutMs,
        controller
      );

      let payload: any = null;
      try {
        payload = await httpResponse.json();
      } catch {
        const text = await httpResponse.text();
        throw new Error(`Invalid JSON response: ${text}`);
      }

      if (!httpResponse.ok) {
        const message =
          payload?.message ??
          `OTP validation failed with status ${httpResponse.status} ${httpResponse.statusText}`;
        throw new Error(message);
      }

      // Gestion spéciale pour httpbin.org (mock endpoint)
      let responseData = payload;
      if (payload?.json) {
        responseData = payload.json;
      }

      const isHttpbin = url.includes("httpbin.org");
      if (isHttpbin) {
        // Simulation : OTP validé avec succès
        const otpResponse: OTPServerResponse = {
          success: true,
          message: "Code OTP validé avec succès",
        };
        
        otpValidated.value = true;
        otpSent.value = true;
        serverResponse.value = otpResponse;
        return otpResponse;
      }

      // Construire la réponse standardisée pour les vrais endpoints
      const otpResponse: OTPServerResponse = {
        success: responseData?.success ?? true,
        call_back_url: responseData?.call_back_url ?? responseData?.callback_url ?? responseData?.callBackUrl,
        message: responseData?.message ?? "Code OTP validé avec succès",
      };

      // Extraire et stocker le token si présent
      const extractedToken = extractAuthToken(responseData);
      if (extractedToken) {
        otpResponse.authToken = extractedToken;
        storeAuthToken(extractedToken);
        console.log('✅ [OTP] Token d\'authentification stocké en mémoire après validation:', {
          key: tokenStorageKey.value,
          tokenKeys: Object.keys(extractedToken),
        });
      }

      otpValidated.value = true;
      otpSent.value = true;
      serverResponse.value = otpResponse;

      return otpResponse;
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Erreur lors de la validation de l'OTP";
      error.value = message;
      throw err;
    } finally {
      isSending.value = false;
    }
  };

  const resetOTPSentState = () => {
    otpSent.value = false;
    serverResponse.value = null;
    error.value = null;
    otpValidated.value = false;
  };

  const resendOTP = async (formData: Record<string, any>): Promise<OTPServerResponse> => {
    if (!options) {
      throw new Error("OTP options are not configured for this field");
    }

    const cooldown = options.resendCooldownSeconds ?? 60;
    
    if (resendCooldown.value > 0) {
      throw new Error(`Veuillez attendre ${resendCooldown.value} secondes avant de renvoyer le code`);
    }

    isResending.value = true;
    error.value = null;

    try {
      // Utiliser la config d'envoi initiale si disponible, sinon la config de vérification
      const sendConfig = options.sendOTPApiConfig || {
        serverDns: options.serverDns,
        postApiEndPoint: options.postApiEndPoint,
        payload: options.payload,
        extraHeaders: options.extraHeaders,
        bearer: options.bearer,
      };

      const controller = new AbortController();
      const url = buildAbsoluteUrl(sendConfig.serverDns, sendConfig.postApiEndPoint);
      
      const basePayload = normalizePayload(sendConfig.payload);
      const mergedPayload = {
        ...basePayload,
        ...formData,
      };

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(sendConfig.extraHeaders ?? {}),
      };

      if (sendConfig.bearer) {
        headers.Authorization = `Bearer ${sendConfig.bearer}`;
      }

      const fetchPromise = fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(mergedPayload),
        signal: controller.signal,
      });

      const response = await withTimeout(
        fetchPromise,
        options.timeoutMs,
        controller
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `OTP resend failed with status ${response.status}`);
      }

      const result = await response.json();
      
      // Démarrer le cooldown
      resendCooldown.value = cooldown;
      if (cooldownInterval) {
        clearInterval(cooldownInterval);
      }
      cooldownInterval = setInterval(() => {
        resendCooldown.value = resendCooldown.value - 1;
        if (resendCooldown.value <= 0) {
          resendCooldown.value = 0;
          if (cooldownInterval) {
            clearInterval(cooldownInterval);
            cooldownInterval = null;
          }
        }
      }, 1000);

      otpSent.value = true;
      return { success: true, message: "Code OTP renvoyé avec succès" };
    } catch (err) {
      const message = err instanceof Error ? err.message : "Erreur lors du renvoi du code";
      error.value = message;
      throw err;
    } finally {
      isResending.value = false;
    }
  };

  const reset = () => {
    isSending.value = false;
    otpSent.value = false;
    otpValidated.value = false;
    serverResponse.value = null;
    error.value = null;
    resendCooldown.value = 0;
    isResending.value = false;
    clearAuthToken(); // Nettoyer le token
    if (cooldownInterval) {
      clearInterval(cooldownInterval);
      cooldownInterval = null;
    }
  };

  return {
    sendOTP,
    validateOTP,
    resendOTP,
    resetOTPSentState,
    handleCallbackRedirect,
    isSending: computed(() => isSending.value),
    isResending: computed(() => isResending.value),
    resendCooldown: computed(() => resendCooldown.value),
    otpSent: computed(() => otpSent.value),
    otpValidated: computed(() => otpValidated.value),
    serverResponse: computed(() => serverResponse.value),
    error: computed(() => error.value),
    reset,
    // Nouvelles fonctions pour le token
    authToken,
    getStoredAuthToken,
    getTokenValue: () => authToken.value ? getTokenValue(authToken.value) : null,
    isTokenValid: () => isTokenValid(authToken.value),
    clearAuthToken,
  };
};

